/**
 * API client utilisateur — attache automatiquement le JWT du user (lib/user-auth).
 *
 * Toutes les requêtes /api/* ajoutent le Bearer token. Les requêtes /dev/* ne
 * l'ajoutent pas (mode dev sans auth). En 401, purge la session et déclenche
 * un événement `mizan:auth-expired` pour que le shell route vers /login.
 */

import { clearUserSession, getUserToken } from './user-auth';
import type { AgentName, InvokeResponseBase } from './types';

const DEFAULT_API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit & { auth?: boolean }): Promise<T> {
  const useAuth = init?.auth !== false && path.startsWith('/api/');
  const token = useAuth ? getUserToken() : null;
  const res = await fetch(`${DEFAULT_API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token.token}` } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  let body: unknown = text;
  try {
    body = JSON.parse(text);
  } catch {
    /* */
  }

  if (!res.ok) {
    if (res.status === 401 && useAuth) {
      clearUserSession();
      window.dispatchEvent(new CustomEvent('mizan:auth-expired'));
    }
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export interface InvokeArgs {
  payload: Record<string, unknown>;
  fiscal_reference_date?: string;
  company_id?: string;
}

export interface RunRow {
  id: string;
  firm_id: string;
  company_id: string | null;
  user_id: string;
  agent_name: string;
  model_used: string;
  status: 'running' | 'completed' | 'awaiting_user_input' | 'failed';
  latency_ms: number | null;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}

export interface RunDetail extends RunRow {
  input_payload: unknown;
  output_payload: unknown;
}

export const api = {
  health: () => request<{ status: string; service: string; environment: string; timestamp: string }>('/health', { auth: false }),

  // En mode user-authenticated → /api/agents. Fallback /dev/agents si pas de token.
  invokeAgent: (agentName: AgentName, args: InvokeArgs) => {
    const hasToken = getUserToken() !== null;
    const path = hasToken
      ? `/api/agents/${agentName}/invoke`
      : `/dev/agents/${agentName}/invoke`;
    return request<InvokeResponseBase>(path, {
      method: 'POST',
      body: JSON.stringify(args),
    });
  },

  queueAgent: (agentName: AgentName, args: InvokeArgs) =>
    request<{ agent_run_id: string; status: 'queued' }>(`/api/agents/${agentName}/queue`, {
      method: 'POST',
      body: JSON.stringify(args),
    }),

  listRuns: (params?: { limit?: number; offset?: number; agent_name?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.agent_name) qs.set('agent_name', params.agent_name);
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ runs: RunRow[]; total: number; limit: number; offset: number }>(`/api/runs${suffix}`);
  },

  getRun: (runId: string) =>
    request<{ run: RunDetail; citations: Array<{ id: string; source_type: string; source_id: string; reference: string; fetched_at: string }> }>(
      `/api/runs/${encodeURIComponent(runId)}`,
    ),

  exportRunUrl: (runId: string) => {
    const token = getUserToken();
    if (!token) return null;
    // Export URL inline avec token (exception : les endpoints /export.html acceptent le token en query)
    return `${DEFAULT_API_BASE}/api/runs/${encodeURIComponent(runId)}/export.html?token=${encodeURIComponent(token.token)}`;
  },

  listCompanies: () => request<{ companies: unknown[] }>('/api/companies'),
};

export { ApiError };
